/**
 * Layer 2: MarketValidationLensModal (Generic Modal Container)
 * 
 * Invariants:
 * 1. Accessible Dialog: role="dialog", aria-modal="true", named title.
 * 2. Focus Trap & Restoration: traps Tab/Shift+Tab within modal, restores focus on close.
 * 3. Background scroll lock & inert isolation: locks document.body scroll and applies `inert`
 *    to all background sibling elements outside the modal portal/container while active.
 * 4. Escape key and backdrop click dismissal.
 * 5. Pure CSS Maximize ([⛶ 最大化] / [🗗 还原]):
 *    Desktop 70vw drawer / Mobile 100vw / Fullscreen 100vw without remounting chart.
 * 6. Mobile 100vw responsive clamp and flex-wrap header (no horizontal scroll overflow).
 * 7. Single active lens ownership across callers: tracked via global module singleton.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface MarketValidationLensModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode;
  onMaximizeChange?: (isMaximized: boolean) => void;
  lensId?: string;
}

// Module-level single active lens ownership coordinator
let activeLensOwnerId: string | null = null;
const lensOwnerListeners = new Set<(activeId: string | null) => void>();

export function getActiveLensOwner(): string | null {
  return activeLensOwnerId;
}

export function registerLensOwner(id: string): () => void {
  activeLensOwnerId = id;
  lensOwnerListeners.forEach(listener => listener(activeLensOwnerId));
  return () => {
    if (activeLensOwnerId === id) {
      activeLensOwnerId = null;
      lensOwnerListeners.forEach(listener => listener(null));
    }
  };
}

export const MarketValidationLensModal: React.FC<MarketValidationLensModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  headerActions,
  onMaximizeChange,
  lensId,
}) => {
  const [isMaximized, setIsMaximized] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const fallbackIdRef = useRef<string>(`lens-${Math.random().toString(36).slice(2, 9)}`);
  const effectiveLensId = lensId || fallbackIdRef.current;

  // Track active owner ID from coordinator
  const [activeOwner, setActiveOwner] = useState<string | null>(() => {
    if (isOpen && (activeLensOwnerId === null || activeLensOwnerId === effectiveLensId)) {
      activeLensOwnerId = effectiveLensId;
      return effectiveLensId;
    }
    return activeLensOwnerId;
  });

  // Subscribe to coordinator listener changes
  useEffect(() => {
    const onOwnerChanged = (newOwner: string | null) => {
      setActiveOwner(newOwner);
    };
    lensOwnerListeners.add(onOwnerChanged);
    return () => {
      lensOwnerListeners.delete(onOwnerChanged);
    };
  }, []);

  // Enforce single active lens ownership registration
  useEffect(() => {
    if (!isOpen) return;
    if (activeLensOwnerId === null || activeLensOwnerId === effectiveLensId) {
      const unregister = registerLensOwner(effectiveLensId);
      return () => {
        unregister();
      };
    }
  }, [isOpen, effectiveLensId, activeOwner]);

  // Is this specific modal instance the allowed active owner?
  const isOwnerActive = isOpen && activeOwner === effectiveLensId;

  // Capture trigger element before opening for focus return
  useEffect(() => {
    if (isOwnerActive) {
      triggerElementRef.current = document.activeElement as HTMLElement;
    } else {
      setIsMaximized(false);
      if (triggerElementRef.current && typeof triggerElementRef.current.focus === 'function') {
        try {
          triggerElementRef.current.focus();
        } catch {
          // ignore focus error
        }
        triggerElementRef.current = null;
      }
    }
  }, [isOwnerActive]);

  // Make background siblings inert and lock body scroll while open
  useEffect(() => {
    if (!isOwnerActive) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Find and set inert on all background elements outside the modal
    const elementsToRestoreInert: HTMLElement[] = [];
    const overlayEl = overlayRef.current;

    if (typeof document !== 'undefined') {
      // 1. Mark top-level elements under document.body (like #root) as inert
      Array.from(document.body.children).forEach(child => {
        if (child instanceof HTMLElement && child !== overlayEl && !child.contains(overlayEl)) {
          if (!child.hasAttribute('inert')) {
            child.setAttribute('inert', '');
            elementsToRestoreInert.push(child);
          }
        }
      });

      // 2. Also ensure #root is marked inert if overlay is mounted outside or alongside it
      const rootEl = document.getElementById('root');
      if (rootEl && rootEl !== overlayEl && !rootEl.contains(overlayEl) && !rootEl.hasAttribute('inert')) {
        rootEl.setAttribute('inert', '');
        elementsToRestoreInert.push(rootEl);
      }
    }

    return () => {
      // Symmetric restoration: only restore body overflow and remove inert
      // if NO OTHER lens is currently active!
      if (activeLensOwnerId === null || activeLensOwnerId === effectiveLensId) {
        document.body.style.overflow = originalOverflow;
        elementsToRestoreInert.forEach(el => {
          el.removeAttribute('inert');
        });
      }
    };
  }, [isOwnerActive, effectiveLensId]);

  // Handle Escape key and focus trap
  useEffect(() => {
    if (!isOwnerActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) {
          e.preventDefault();
          modalRef.current.focus();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // If focus is on first element OR on dialog container itself, wrap to last element
          if (
            document.activeElement === firstElement ||
            document.activeElement === modalRef.current ||
            !modalRef.current.contains(document.activeElement)
          ) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // If focus is on last element, wrap to first element
          if (
            document.activeElement === lastElement ||
            !modalRef.current.contains(document.activeElement)
          ) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOwnerActive, onClose]);

  // Focus dialog on open
  useEffect(() => {
    if (isOwnerActive && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOwnerActive]);

  const toggleMaximize = useCallback(() => {
    setIsMaximized(prev => {
      const next = !prev;
      if (onMaximizeChange) {
        onMaximizeChange(next);
      }
      return next;
    });
  }, [onMaximizeChange]);

  if (!isOpen || !isOwnerActive) return null;

  const modalElement = (
    <div
      ref={overlayRef}
      data-testid="market-validation-lens-overlay"
      className="market-validation-lens-overlay"
      onClick={e => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(5, 10, 8, 0.75)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
        transition: 'background-color 0.2s ease',
      }}
    >
      {/* Modal Drawer Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="market-validation-lens-title"
        tabIndex={-1}
        data-testid="market-validation-lens-dialog"
        className={`market-validation-lens-dialog ${isMaximized ? 'maximized' : ''}`}
        style={{
          width: isMaximized
            ? '100vw'
            : typeof window !== 'undefined' && window.innerWidth <= 768
            ? '100vw'
            : 'clamp(360px, 70vw, 1400px)',
          maxWidth: '100vw',
          height: '100vh',
          backgroundColor: 'var(--bg-surface, #121915)',
          borderLeft: '1px solid var(--border-subtle, #1f2b23)',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'width 0.25s ease-in-out',
        }}
      >
        <style>{`
          @media (max-width: 768px) {
            .market-validation-lens-dialog {
              width: 100vw !important;
              max-width: 100vw !important;
            }
            .lens-modal-header-responsive {
              padding: 8px 10px !important;
              flex-wrap: wrap !important;
              gap: 8px !important;
            }
          }
        `}</style>

        {/* Header Bar */}
        <div
          data-testid="lens-modal-header"
          className="lens-modal-header-responsive"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid var(--border-subtle, #1f2b23)',
            backgroundColor: 'var(--bg-canvas, #0f1411)',
            boxSizing: 'border-box',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Title Area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flexShrink: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <span style={{ fontSize: '15px', flexShrink: 0 }}>🔍</span>
              <h2
                id="market-validation-lens-title"
                style={{
                  margin: 0,
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'var(--text-primary, #e6eee8)',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </h2>
            </div>
            {subtitle && (
              <span
                style={{
                  fontSize: '10px',
                  color: 'var(--text-muted, #8b9b90)',
                  fontFamily: 'var(--font-mono, monospace)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {subtitle}
              </span>
            )}
          </div>

          {/* Action Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {headerActions}

            {/* Maximize Toggle Button */}
            <button
              type="button"
              data-testid="lens-maximize-toggle-btn"
              onClick={toggleMaximize}
              title={isMaximized ? '还原视窗 (Restore)' : '最大化视窗 (Maximize)'}
              aria-label={isMaximized ? '还原视窗' : '最大化视窗'}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid var(--border-subtle, #1f2b23)',
                color: 'var(--text-secondary, #b4c2b9)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono, monospace)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span>{isMaximized ? '🗗' : '⛶'}</span>
              <span>{isMaximized ? '还原' : '最大化'}</span>
            </button>

            {/* Close Button */}
            <button
              type="button"
              data-testid="lens-close-btn"
              onClick={onClose}
              title="关闭透镜 (Esc)"
              aria-label="关闭透镜"
              style={{
                backgroundColor: 'transparent',
                border: '1px solid var(--border-subtle, #1f2b23)',
                color: 'var(--text-muted, #8b9b90)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '14px',
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Body / Chart Viewport */}
        <div
          data-testid="lens-modal-body"
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalElement, document.body);
  }
  return modalElement;
};
